import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  useUsersMeRetrieve,
  usersImageCreate,
  usersMePartialUpdate,
} from "@/api/generated/backendAPI";
import { toast } from "react-toastify";
import UserImage from "@/components/ui/UserImage";
import UserImageUpload from "@/components/profile/UserImageUpload";
import { queryClient } from "@/main";
import { useSidebarContent } from "@/providers/useSidebar";
import { CompetitionListSidebar } from "@/components/layout/CompetitionListSidebar";
import { IoChevronBackOutline } from "react-icons/io5";

export const Route = createFileRoute("/_layout/_auth/profile/")({
  component: MyProfile,
});

function MyProfile() {
  const { data: user } = useUsersMeRetrieve();
  const [editing, setEditing] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [imageURL, setImageURL] = useState("");
  const [displayName, setDisplayName] = useState<string>("");
  useSidebarContent(<CompetitionListSidebar />, []);

  const handleImageFile = (file: File) => {
    setFile(file);
  };
  const handleImageURL = (url: string) => {
    setImageURL(url);
  };
  const resetEdit = (name: string) => {
    setFile(null);
    setDisplayName(name);
  };
  useEffect(() => {
    if (user) {
      const initialDisplayName = user.displayName
        ? user.displayName
        : user.name;
      setDisplayName(initialDisplayName);
    }
  }, [user]);

  const handleClickSave = async () => {
    if (!user) return;
    const res = { change: false, err: false };
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        if (reader.result && typeof reader.result === "string") {
          try {
            await usersImageCreate(user.id, { file: reader.result });
            queryClient.invalidateQueries(); // キャッシュを無効化
            res.change = true;
          } catch (e) {
            res.err = true;
          }
        }
      };
    }
    if (displayName && displayName !== user.displayName) {
      try {
        await usersMePartialUpdate({ displayName });
        res.change = true;
      } catch (e) {
        res.err = true;
      }
    }
    if (res.err) {
      toast.error("更新にエラーがあります。", {
        position: "bottom-left",
      });
    } else if (res.change) {
      toast.success("更新しました。", {
        position: "bottom-left",
      });
    }
  };

  return (
    <>
      <Link to="/competitions" className="flex items-center gap-1 text-sm text-navcolor hover:opacity-70 mb-2">
        <IoChevronBackOutline size={16} /><span>一覧に戻る</span>
      </Link>
      <h1>プロフィール</h1>
      <div className="flex flex-col items-start justify-start gap-4">
        <div className="w-full bg-white p-4 rounded-md shadow-xs-slate-200">
          <div>
            <h2 className="font-bold">公開情報</h2>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-16">
              <div className="relative size-28">
                {editing ? (
                  <UserImageUpload
                    postImage={handleImageFile}
                    setImageURL={handleImageURL}
                  />
                ) : !editing && imageURL ? (
                  <div className="size-28 rounded-full overflow-hidden">
                    <img src={imageURL} className="object-cover min-w-full" />
                  </div>
                ) : (
                  user && (
                    <UserImage userId={user.id} noImageType="noimage" size={28} />
                  )
                )}
              </div>
              <div>
                <p className="text-sm text-slate-600">表示名</p>
                {editing ? (
                  <div className="tracking-wide py-2 pr-2 text-lg">
                    <input
                      type="text"
                      value={displayName}
                      className="min-w-40 px-4 py-2 border border-gray-300 rounded-md"
                      onChange={(e) => setDisplayName(e.target.value)}
                    />
                  </div>
                ) : (
                  <p className="tracking-wide py-2 pr-2 text-lg">
                    {displayName}
                  </p>
                )}
              </div>
            </div>
            <div>
              {!editing ? (
                <button className="" onClick={() => setEditing(true)}>
                  編集
                </button>
              ) : (
                <div className="flex items-center gap-8 shrink-0">
                  <button
                    className=""
                    onClick={() => {
                      handleClickSave();
                      resetEdit(displayName);
                      setEditing(false);
                    }}
                  >
                    保存
                  </button>
                  <button
                    className="bg-white text-maincolor boder border-maincolor"
                    onClick={() => {
                      const initialDisplayName = user?.displayName
                        ? user.displayName
                        : user?.name;
                      resetEdit(initialDisplayName || "");
                      setEditing(false);
                    }}
                  >
                    キャンセル
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* start 基本情報 */}
        <div className="w-full bg-white p-4 rounded-md shadow-xs-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="font-bold">基本情報</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-[repeat(2,minmax(max-content,360px))] gap-4 mt-4">
            <div className="w-max">
              <p className="text-sm text-slate-600">メールアドレス</p>
              {
                <p className={"tracking-wide py-2 pr-2 text-lg"}>
                  {user?.email}
                </p>
              }
            </div>
            <div className="w-max">
              <p className="text-sm text-slate-600">パスワード</p>
              {
                <p className={"tracking-wide py-2 pr-2 text-lg"}>
                  <Link to="/profile/change-password">
                    パスワードを変更する
                  </Link>
                </p>
              }
            </div>
          </div>
        </div>
        {/* end 基本情報 */}
        {/* start 組織情報 ※編集なし */}
        <div className="w-full bg-white p-4 rounded-md shadow-xs-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="font-bold">組織情報</h2>
          </div>
          <div className="mt-4">
            {user?.organizations.map((data, index) => {
              const multiOrganizationSuffix =
                user.organizations.length > 1
                  ? " " + (index + 1).toString()
                  : "";
              return (
                <div key={data.organizationId + data.departmentId} className="grid grid-cols-2 md:grid-cols-[repeat(2,minmax(max-content,360px))] gap-4">
                  <div
                    className="w-max"
                    key={data.organizationId + index.toString()}
                  >
                    <p className="text-sm text-slate-600">
                      所属組織{multiOrganizationSuffix}
                    </p>
                    {
                      <p className={"tracking-wide py-2 pr-2 text-lg"}>
                        {data.organizationName}
                      </p>
                    }
                  </div>
                  <div
                    className="w-max"
                    key={data.departmentId + index.toString()}
                  >
                    <p className="text-sm text-slate-600">
                      部署名{multiOrganizationSuffix}
                    </p>
                    {
                      <p className={"tracking-wide py-2 pr-2 text-lg"}>
                        {data.departmentName}
                      </p>
                    }
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* end 組織情報 */}
        {/* start 獲得メダル */}
        <div className="w-full bg-white p-4 rounded-md shadow-xs-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="font-bold">獲得メダル</h2>
          </div>
        </div>
        {/* end 基本情報 */}
        {/* start コンペティション情報 */}
        <div className="w-full bg-white p-4 rounded-md shadow-xs-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="font-bold">参加コンペティション</h2>
          </div>
        </div>
        {/* end 基本情報 */}
      </div>
    </>
  );
}
